import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../api";
import Layout from "../components/Layout";
import { useModules } from "../modules";
import { SqueletteTableau } from "../components/Squelette";
import { ROLES, labelRole } from "../roles";
import Notification from "../components/Notification";

// Reprise du texte de la politique serveur (auth.valider_mot_de_passe). Il est
// affiche pour guider, PAS pour valider : le serveur reste seul juge, et c'est
// lui qui renvoie le motif exact du refus. Un controle ici ne protegerait rien
// -- il suffit d'appeler l'API directement pour le contourner.
const AIDE_MOT_DE_PASSE =
  "12 caractères minimum, sans suite prévisible (1234, abcd), " +
  "sans mot courant, et sans votre adresse e-mail.";

// LE DÉFAUT EST LE RÔLE LE MOINS PRIVILÉGIÉ. Il a été « admin » un temps, au
// motif que le premier compte doit pouvoir gérer les suivants : un second
// compte est alors reparti administrateur sans que personne l'ait voulu. Un
// défaut dangereux qui ne se voit pas est pire qu'une case à cocher.
const NOUVEAU_VIDE = {
  nom: "",
  prenom: "",
  email: "",
  mot_de_passe: "",
  role: "travailleur",
};

/** Extrait le message du serveur — c'est lui qui dit pourquoi il a refuse. */
function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

export default function Utilisateurs({ user, onLogout }) {
  // Le libre-service de creation est une option, accordee client par client.
  // Le masquage n'est PAS la protection -- la route repond 403 de toute facon
  // -- mais un bouton qui mene a un refus donne l'impression d'un logiciel
  // casse plutot que d'une option non souscrite.
  const modules = useModules();

  const [liste, setListe] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");

  const [nouveau, setNouveau] = useState(NOUVEAU_VIDE);
  const [creationOuverte, setCreationOuverte] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const [modalMdp, setModalMdp] = useState(null); // utilisateur cible
  const [mdpNouveau, setMdpNouveau] = useState("");


  const charger = useCallback(() => {
    setChargement(true);
    api
      .get("/utilisateurs")
      .then(({ data }) => setListe(data))
      .catch((err) => setErreur(messageErreur(err, "Impossible de charger les comptes.")))
      .finally(() => setChargement(false));
  }, []);

  useEffect(charger, [charger]);

  // Un travailleur qui tape l'URL a la main n'a rien a faire ici. Ce n'est PAS
  // une protection -- le serveur repond 403 de toute facon -- seulement une
  // facon de ne pas afficher un ecran vide et incomprehensible.
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const annoncer = (texte) => {
    setMessage(texte);
    setErreur("");
    setTimeout(() => setMessage(""), 6000);
  };

  const creer = (e) => {
    e.preventDefault();
    setEnCours(true);
    setErreur("");
    api
      .post("/utilisateurs", nouveau)
      .then(({ data }) => {
        setNouveau(NOUVEAU_VIDE);
        setCreationOuverte(false);
        annoncer(`Compte créé pour ${data.prenom} ${data.nom}.`);
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Création impossible.")))
      .finally(() => setEnCours(false));
  };

  const basculerActif = (cible) => {
    const desactive = cible.actif;
    setErreur("");
    api
      .patch(`/utilisateurs/${cible.id}`, { actif: !cible.actif })
      .then(() => {
        annoncer(
          desactive
            ? `${cible.prenom} ${cible.nom} est désactivé : ses sessions en cours sont coupées.`
            : `${cible.prenom} ${cible.nom} peut de nouveau se connecter.`
        );
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Modification impossible.")));
  };

  const changerRole = (cible, role) => {
    setErreur("");
    api
      .patch(`/utilisateurs/${cible.id}`, { role })
      .then(() => {
        annoncer(
          `${cible.prenom} ${cible.nom} est désormais ${labelRole(role).toLowerCase()} — ` +
            "ses sessions en cours sont coupées."
        );
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Modification impossible.")));
  };

  const reinitialiser = () => {
    setEnCours(true);
    setErreur("");
    api
      .post(`/utilisateurs/${modalMdp.id}/mot-de-passe`, { nouveau_mot_de_passe: mdpNouveau })
      .then(() => {
        annoncer(
          `Mot de passe de ${modalMdp.prenom} ${modalMdp.nom} réinitialisé : ` +
            "ses sessions en cours sont coupées. Transmettez-le-lui de vive voix."
        );
        setModalMdp(null);
        setMdpNouveau("");
      })
      .catch((err) => setErreur(messageErreur(err, "Réinitialisation impossible.")))
      .finally(() => setEnCours(false));
  };


  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h1>Utilisateurs</h1>
        <div className="actions-groupe">
          {/* Le changement de SON propre mot de passe vit dans « Mon compte »,
              accessible a tout le monde. Le garder ici en plus ferait deux
              definitions du meme geste, et un travailleur n'y aurait toujours
              pas acces. */}
          <Link className="btn" to="/mon-compte">
            Mon compte
          </Link>
          {modules.gestion_comptes_client && (
            <button
              className="btn-accent"
              onClick={() => {
                setCreationOuverte((v) => !v);
                setErreur("");
              }}
            >
              {creationOuverte ? "Annuler" : "+ Ajouter un utilisateur"}
            </button>
          )}
        </div>
      </div>

      <Notification message={message} />
      {erreur && <p className="erreur">{erreur}</p>}

      {/* Dire POURQUOI le bouton n'est pas la. Un ecran qui se contente de ne
          rien proposer se lit comme une fonction manquante, et l'utilisateur
          cherche la ou il n'y a rien. */}
      {modules.charge && !modules.gestion_comptes_client && (
        <p className="sous-info">
          L’ouverture d’un compte passe par votre prestataire — contactez-le
          pour un nouvel accès. Vous gardez la gestion des comptes ci-dessous :
          mot de passe, rôle et désactivation d’un départ.
        </p>
      )}

      {creationOuverte && (
        <form className="formulaire" onSubmit={creer}>
          <div className="grille-form">
            <label className="champ">
              <span className="champ-label">Prénom *</span>
              <input
                required
                value={nouveau.prenom}
                onChange={(e) => setNouveau({ ...nouveau, prenom: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Nom *</span>
              <input
                required
                value={nouveau.nom}
                onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">E-mail *</span>
              <input
                required
                type="email"
                value={nouveau.email}
                onChange={(e) => setNouveau({ ...nouveau, email: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Rôle *</span>
              <select
                value={nouveau.role}
                onChange={(e) => setNouveau({ ...nouveau, role: e.target.value })}
              >
                {ROLES.map((r) => (
                  <option key={r.valeur} value={r.valeur}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="champ">
              <span className="champ-label">Mot de passe *</span>
              <input
                required
                type="text"
                value={nouveau.mot_de_passe}
                onChange={(e) => setNouveau({ ...nouveau, mot_de_passe: e.target.value })}
              />
            </label>
          </div>
          <p className="sous-info">
            {AIDE_MOT_DE_PASSE} Il est affiché en clair pour que vous puissiez
            le transmettre — de vive voix de préférence.
          </p>
          <div className="actions-form">
            <button className="btn-accent" type="submit" disabled={enCours}>
              {enCours ? "Création…" : "Créer le compte"}
            </button>
          </div>
        </form>
      )}

      {chargement && (
        <table className="tableau">
          <thead>
            <tr>
              <th>Nom</th>
              <th>E-mail</th>
              <th>Rôle</th>
              <th>État</th>
              <th>Actions</th>
            </tr>
          </thead>
          <SqueletteTableau lignes={4} colonnes={5} />
        </table>
      )}

      {!chargement && liste && liste.length === 0 && (
        <p className="vide">Aucun compte. Ajoutez-en un pour donner accès à l’application.</p>
      )}

      {!chargement && liste && liste.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <th>Nom</th>
              <th>E-mail</th>
              <th>Rôle</th>
              <th>État</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {liste.map((u) => (
              <tr key={u.id}>
                <td>
                  <strong>
                    {u.prenom} {u.nom}
                  </strong>
                  {u.id === user.id && <span className="sous-info"> · vous</span>}
                </td>
                <td>{u.email}</td>
                <td>
                  <select
                    value={u.role}
                    disabled={u.id === user.id}
                    onChange={(e) => changerRole(u, e.target.value)}
                  >
                    {ROLES.map((r) => (
                      <option key={r.valeur} value={r.valeur}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {u.actif ? (
                    <span className="badge">Actif</span>
                  ) : (
                    <span className="badge-exclu">Désactivé</span>
                  )}
                </td>
                <td className="actions-cellule">
                  <button className="lien-action" onClick={() => setModalMdp(u)}>
                    Réinitialiser le mot de passe
                  </button>
                  {u.id !== user.id && (
                    <button
                      className={u.actif ? "lien-danger" : "lien-action"}
                      onClick={() => basculerActif(u)}
                    >
                      {u.actif ? "Désactiver" : "Réactiver"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="sous-info" style={{ marginTop: 24 }}>
        Un compte n’est jamais supprimé : il est désactivé. L’historique des
        véhicules, inspections et opérations continue de le référencer.
        Désactiver un compte, changer son rôle ou son mot de passe coupe
        immédiatement ses sessions en cours.
      </p>

      {modalMdp && (
        <div className="modal-overlay" onClick={() => setModalMdp(null)}>
          <div className="modal-carte" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-titre">Réinitialiser le mot de passe</h3>
            <p className="modal-sous-titre">
              {modalMdp.prenom} {modalMdp.nom} — {modalMdp.email}
            </p>
            <label className="modal-champ">
              Nouveau mot de passe
              <input
                type="text"
                value={mdpNouveau}
                onChange={(e) => setMdpNouveau(e.target.value)}
                placeholder="12 caractères minimum"
              />
            </label>
            <p className="sous-info">{AIDE_MOT_DE_PASSE}</p>
            {erreur && <p className="erreur">{erreur}</p>}
            <div className="modal-actions">
              <button className="btn" onClick={() => setModalMdp(null)}>
                Annuler
              </button>
              <button
                className="btn-accent"
                disabled={enCours || !mdpNouveau}
                onClick={reinitialiser}
              >
                {enCours ? "En cours…" : "Réinitialiser"}
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}
